import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Collapse,
  FormControl,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import KpiCard from "../commercial-dashboard/KpiCard";
import { KpiGrid, Section } from "../commercial-dashboard/DashboardBits";
import {
  CHANNELS,
  CM_CHANNELS,
  MONTHS,
  MONTHS_SHORT,
  SEASONS,
  eur,
  groupBy,
  mean,
  modelLabel,
  pct,
  realUnitCost,
  subformatLabel,
  sum,
  toneOfMarginDiff,
  unitMoney,
} from "./controlBoardModel";
import { ChannelSwatch, Note, Num, Signal } from "./ControlBoardBits";
import { TabProps } from "./boardData";
import { cleanPrices } from "./PricesTab";
import { ControlBoardChannel, ControlBoardRow } from "../../types/controlBoard";

interface Piece {
  key: string;
  channel: ControlBoardChannel;
  subformat: string | null;
  objective: string;
  lines: ControlBoardRow[];
  price: number;
  cost: number;
  margin: number;
  target: number;
  minPrice: number;
}

/** Márgenes: precio de venta frente a coste de plataforma y precio mínimo para el margen objetivo. */
const MarginsTab: React.FC<TabProps> = ({ data }) => {
  const theme = useTheme();
  const [mode, setMode] = useState<"bp" | "fixed">("bp");
  const [fixed, setFixed] = useState(49);
  const [channel, setChannel] = useState<ControlBoardChannel | "">("");
  const [selected, setSelected] = useState("");
  const selRef = useRef<HTMLDivElement>(null);

  /** Margen del Business Plan de cada canal de compra de medios. */
  const planMargins = useMemo(() => {
    const out: Partial<Record<ControlBoardChannel, number>> = {};
    CM_CHANNELS.forEach((c) => {
      const lines = data.plan.filter(
        (l) => l.channel === c && l.payer === "TI_MEDIA_GROUP_SL"
      );
      const inv = sum(lines, (l) => l.managedInvestment);
      if (inv > 0) out[c] = sum(lines, (l) => l.campaignProfit) / inv;
    });
    return out;
  }, [data.plan]);
  const targetFor = (c: ControlBoardChannel) =>
    mode === "fixed" || planMargins[c] === undefined
      ? fixed / 100
      : (planMargins[c] as number);

  const { ok } = useMemo(() => cleanPrices(data.rows), [data]);
  const costed = ok.filter((r) => r.volume > 0 && r.realCost > 0);
  const pieces: Piece[] = [
    ...groupBy(
      costed,
      (r) => `${r.channel}|${r.subformat}|${r.objective}`
    ).entries(),
  ]
    .map(([key, lines]) => {
      const price = mean(lines.map((r) => r.grossUnitCost));
      const cost = mean(lines.map(realUnitCost));
      const target = targetFor(lines[0].channel);
      return {
        key,
        channel: lines[0].channel,
        subformat: lines[0].subformat,
        objective: lines[0].objective,
        lines,
        price,
        cost,
        margin: price ? 1 - cost / price : NaN,
        target,
        minPrice: target < 1 ? cost / (1 - target) : NaN,
      };
    })
    .sort(
      (a, b) =>
        CHANNELS.indexOf(a.channel) - CHANNELS.indexOf(b.channel) ||
        subformatLabel(a.subformat).localeCompare(
          subformatLabel(b.subformat),
          "es"
        )
    );
  const shown = pieces.filter((p) => !channel || p.channel === channel);
  const scope = costed.filter((r) => !channel || r.channel === channel);
  const sale = sum(scope, (r) => r.sale);
  const cost = sum(scope, (r) => r.realCost);
  const belowMin = shown.filter((p) => p.price < p.minPrice);
  const piece = pieces.find((p) => p.key === selected);

  return (
    <Stack gap={2}>
      <Section
        title="Márgenes"
        subtitle="Precio de venta frente a coste de plataforma: qué margen deja cada pieza y cuál es el precio mínimo para llegar al margen objetivo. Compra de medios."
        action={
          <Stack direction="row" gap={1.5} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel id="mg-mode">Margen objetivo</InputLabel>
              <Select
                labelId="mg-mode"
                label="Margen objetivo"
                value={mode}
                onChange={(e) => setMode(e.target.value as "bp" | "fixed")}
              >
                <MenuItem value="bp">
                  El del Business Plan de cada formato
                </MenuItem>
                <MenuItem value="fixed">Fijo para todos</MenuItem>
              </Select>
            </FormControl>
            {mode === "fixed" && (
              <TextField
                size="small"
                type="number"
                label="Margen"
                value={fixed}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (Number.isFinite(v) && v >= 0 && v < 95) setFixed(v);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
                sx={{ width: 110 }}
              />
            )}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="mg-f">Formato</InputLabel>
              <Select
                labelId="mg-f"
                label="Formato"
                value={channel}
                onChange={(e) =>
                  setChannel(e.target.value as ControlBoardChannel | "")
                }
              >
                <MenuItem value="">Todos</MenuItem>
                {CM_CHANNELS.filter((c) =>
                  pieces.some((p) => p.channel === c)
                ).map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        }
      >
        <KpiGrid columns={3}>
          <KpiCard
            label="Margen real"
            value={pct(sale ? 1 - cost / sale : NaN)}
            caption={`${eur(sale - cost)} de beneficio sobre ${eur(sale)}`}
          />
          <KpiCard
            label="Piezas"
            value={String(shown.length)}
            caption={`${scope.length} líneas con precio y coste`}
          />
          <KpiCard
            label="Por debajo del precio mínimo"
            value={String(belowMin.length)}
            caption="Piezas cuyo precio medio no llega al margen objetivo con su coste"
            tone={belowMin.length ? "warning" : undefined}
          />
        </KpiGrid>
        {mode === "bp" && Object.keys(planMargins).length === 0 && (
          <Note>
            No hay Business Plan con margen por formato: se usa el {fixed} %
            para todos.
          </Note>
        )}
      </Section>

      <Section
        title="Precio frente a coste por pieza"
        subtitle="Margen real de cada pieza y precio mínimo para llegar al margen objetivo con su coste de plataforma. Pulsa una fila para ver el mínimo mes a mes y el margen de cada cliente."
      >
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Pieza</TableCell>
                <TableCell>Modelo</TableCell>
                <Num>Líneas</Num>
                <Num>Precio medio</Num>
                <Num>Coste plataforma</Num>
                <Num>Margen real</Num>
                <Num>Margen objetivo</Num>
                <Num>Precio mínimo</Num>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((p, i) => (
                <React.Fragment key={p.key}>
                  {(i === 0 || shown[i - 1].channel !== p.channel) && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ pt: 2, fontWeight: 700 }}>
                        <ChannelSwatch channel={p.channel} />
                        {p.channel}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow
                    hover
                    onClick={() => {
                      setSelected(selected === p.key ? "" : p.key);
                      setTimeout(
                        () =>
                          selRef.current?.scrollIntoView?.({
                            behavior: "smooth",
                            block: "start",
                          }),
                        0
                      );
                    }}
                    sx={{
                      cursor: "pointer",
                      boxShadow:
                        selected === p.key
                          ? `inset 3px 0 0 ${theme.palette.text.primary}`
                          : undefined,
                    }}
                  >
                    <TableCell sx={{ pl: 4 }}>
                      {subformatLabel(p.subformat)}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {modelLabel(p.objective)}
                    </TableCell>
                    <Num>{p.lines.length}</Num>
                    <Num>{unitMoney(p.price)}</Num>
                    <Num muted>{unitMoney(p.cost)}</Num>
                    <Num>
                      <Signal
                        text={pct(p.margin, 1)}
                        tone={toneOfMarginDiff(p.margin - p.target)}
                      />
                    </Num>
                    <Num muted>{pct(p.target, 0)}</Num>
                    <Num sx={{ fontWeight: 600 }}>{unitMoney(p.minPrice)}</Num>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Box>
        {shown.length === 0 && (
          <Note>No hay líneas con precio y coste en {data.year}.</Note>
        )}
      </Section>

      <div ref={selRef} />
      {piece && (
        <PieceDetail
          piece={piece}
          rows={data.rows}
          onClose={() => setSelected("")}
        />
      )}

      <SubformatExplorer rows={ok} />
    </Stack>
  );
};

const PieceDetail: React.FC<{
  piece: Piece;
  rows: ControlBoardRow[];
  onClose: () => void;
}> = ({ piece, rows, onClose }) => {
  const monthly = rows.filter(
    (r) =>
      r.channel === piece.channel &&
      r.subformat === piece.subformat &&
      r.objective === piece.objective &&
      r.volume > 0 &&
      r.realCost > 0
  );
  const byMonth = groupBy(monthly, (r) => r.month);
  return (
    <Section
      title={`${piece.channel} · ${subformatLabel(piece.subformat)} · ${modelLabel(piece.objective)}`}
      subtitle={`Precio medio ${unitMoney(piece.price)} · coste de plataforma medio ${unitMoney(piece.cost)} · precio mínimo para un ${pct(piece.target, 0)} de margen: ${unitMoney(piece.minPrice)}.`}
      action={
        <Link component="button" onClick={onClose} underline="hover">
          Cerrar
        </Link>
      }
    >
      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Precio mínimo mes a mes
          </Typography>
          <Note>
            Con el coste de plataforma medio de cada mes. En temporada alta el
            coste sube y el mínimo también.
          </Note>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Mes</TableCell>
                <TableCell>Temporada</TableCell>
                <Num>Líneas</Num>
                <Num>Coste plataforma</Num>
                <Num>Precio mínimo</Num>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
                const list = byMonth.get(m) ?? [];
                const c = mean(list.map(realUnitCost));
                return (
                  <TableRow
                    key={m}
                    sx={{
                      color: list.length < 3 ? "text.disabled" : undefined,
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{MONTHS[m]}</TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {SEASONS[m] ?? ""}
                    </TableCell>
                    {list.length ? (
                      <>
                        <Num>{list.length}</Num>
                        <Num muted>{unitMoney(c)}</Num>
                        <Num sx={{ fontWeight: 600 }}>
                          {unitMoney(c / (1 - piece.target))}
                        </Num>
                      </>
                    ) : (
                      <TableCell colSpan={3} sx={{ color: "text.secondary" }}>
                        Sin datos
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Margen por cliente
          </Typography>
          <Note>Precio, coste y margen de cada cliente en esta pieza.</Note>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <Num>Líneas</Num>
                <Num>Precio medio</Num>
                <Num>Coste</Num>
                <Num>Margen</Num>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...groupBy(piece.lines, (r) => r.client).entries()]
                .map(([client, list]) => {
                  const price = mean(list.map((r) => r.grossUnitCost));
                  const c = mean(list.map(realUnitCost));
                  return {
                    client,
                    n: list.length,
                    price,
                    c,
                    m: price ? 1 - c / price : NaN,
                  };
                })
                .sort((a, b) => a.m - b.m)
                .map((o) => (
                  <TableRow key={o.client}>
                    <TableCell sx={{ fontWeight: 600 }}>{o.client}</TableCell>
                    <Num>{o.n}</Num>
                    <Num>{unitMoney(o.price)}</Num>
                    <Num muted>{unitMoney(o.c)}</Num>
                    <Num>
                      <Signal
                        text={pct(o.m, 1)}
                        tone={toneOfMarginDiff(o.m - piece.target)}
                      />
                    </Num>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Section>
  );
};

const SubformatExplorer: React.FC<{ rows: ControlBoardRow[] }> = ({ rows }) => {
  const options = useMemo(
    () =>
      [...groupBy(rows, (r) => `${r.channel}|${r.subformat ?? ""}`).entries()]
        .map(([key, list]) => ({
          key,
          label: `${list[0].channel} · ${subformatLabel(list[0].subformat)}`,
          n: list.length,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, "es")),
    [rows]
  );
  const [key, setKey] = useState("");
  const [query, setQuery] = useState("");
  const [objective, setObjective] = useState("");
  const [showAll, setShowAll] = useState(false);
  const current =
    key && options.some((o) => o.key === key) ? key : (options[0]?.key ?? "");
  const q = query.trim().toLowerCase();
  const base = q
    ? rows.filter((r) =>
        `${r.lineName} ${r.briefingName} ${r.subformat ?? ""}`
          .toLowerCase()
          .includes(q)
      )
    : rows.filter((r) => `${r.channel}|${r.subformat ?? ""}` === current);
  const objectives = [...groupBy(base, (r) => r.objective).entries()]
    .sort((a, b) => b[1].length - a[1].length)
    .map(([o]) => o);
  const obj = objectives.includes(objective)
    ? objective
    : (objectives[0] ?? "");
  const list = base.filter((r) => r.objective === obj);
  const prices = list.map((r) => r.grossUnitCost);
  const sale = sum(list, (r) => r.sale);
  const cost = sum(list, (r) => r.realCost);

  return (
    <Section
      title="Precio, coste y margen por subformato"
      subtitle="Elige un subformato (o busca por nombre de línea) y mira a qué precio se ha vendido a cada cliente y con qué coste."
      action={
        <Stack direction="row" gap={1.5} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 240 }} disabled={!!q}>
            <InputLabel id="tf-sub">Subformato</InputLabel>
            <Select
              labelId="tf-sub"
              label="Subformato"
              value={current}
              onChange={(e) => setKey(e.target.value)}
            >
              {options.map((o) => (
                <MenuItem key={o.key} value={o.key}>
                  {o.label} ({o.n})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="o busca"
            placeholder="p. ej. skin"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="tf-obj">Modelo</InputLabel>
            <Select
              labelId="tf-obj"
              label="Modelo"
              value={obj}
              onChange={(e) => setObjective(e.target.value)}
            >
              {objectives.map((o) => (
                <MenuItem key={o} value={o}>
                  {modelLabel(o)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      }
    >
      <KpiGrid columns={4}>
        <KpiCard
          label="Precio medio"
          value={unitMoney(mean(prices))}
          caption={`${list.length} líneas · ${new Set(list.map((r) => r.clientId)).size} clientes`}
        />
        <KpiCard
          label="Precio mín. – máx."
          value={
            prices.length
              ? `${unitMoney(Math.min(...prices))} – ${unitMoney(Math.max(...prices))}`
              : "—"
          }
          caption="Entre todas las líneas"
        />
        <KpiCard
          label="Coste real medio"
          value={unitMoney(
            mean(list.filter((r) => r.volume > 0).map(realUnitCost))
          )}
          caption={`Previsto ${unitMoney(mean(list.map((r) => r.netUnitCost)))} · coste total ${eur(cost)}`}
        />
        <KpiCard
          label="Margen total"
          value={pct(sale ? 1 - cost / sale : NaN)}
          caption={`${eur(sale - cost)} sobre ${eur(sale)} facturados`}
        />
      </KpiGrid>

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
        Precio y coste por cliente
      </Typography>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <Num>Precio de venta</Num>
              <Num>Coste</Num>
              <Num>Margen</Num>
              <Num>Líneas</Num>
              <TableCell>Meses</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...groupBy(list, (r) => r.client).entries()]
              .sort(
                (a, b) => sum(b[1], (r) => r.sale) - sum(a[1], (r) => r.sale)
              )
              .flatMap(([client, l]) =>
                [...groupBy(l, (r) => r.grossUnitCost.toFixed(3)).entries()]
                  .sort((a, b) => Number(b[0]) - Number(a[0]))
                  .map(([price, pl], i) => {
                    const s = sum(pl, (r) => r.sale);
                    const c = sum(pl, (r) => r.realCost);
                    return (
                      <TableRow key={`${client}-${price}`}>
                        <TableCell
                          sx={{ fontWeight: i === 0 ? 600 : undefined }}
                        >
                          {i === 0 ? client : ""}
                        </TableCell>
                        <Num sx={{ fontWeight: 600 }}>
                          {unitMoney(Number(price))}
                        </Num>
                        <Num muted>
                          {unitMoney(
                            mean(
                              pl.filter((r) => r.volume > 0).map(realUnitCost)
                            )
                          )}
                        </Num>
                        <Num>{pct(s ? 1 - c / s : NaN, 1)}</Num>
                        <Num>{pl.length}</Num>
                        <TableCell sx={{ color: "text.secondary" }}>
                          {[
                            ...new Set(pl.map((r) => MONTHS_SHORT[r.month])),
                          ].join(", ")}
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
          </TableBody>
        </Table>
      </Box>

      <Link
        component="button"
        underline="hover"
        sx={{ mt: 2, fontWeight: 600 }}
        onClick={() => setShowAll((v) => !v)}
      >
        {showAll ? "Ocultar las líneas" : "Ver todas las líneas"}
      </Link>
      <Collapse in={showAll} unmountOnExit>
        <TableContainer sx={{ maxHeight: "60vh", mt: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Mes</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Línea</TableCell>
                <TableCell>Técnico</TableCell>
                <Num>Precio venta</Num>
                <Num>Coste unitario</Num>
                <Num>Facturación</Num>
                <Num>Coste</Num>
                <Num>Margen</Num>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...list]
                .sort(
                  (a, b) =>
                    b.grossUnitCost - a.grossUnitCost || a.month - b.month
                )
                .map((r) => (
                  <TableRow key={r.lineId} hover>
                    <TableCell>{MONTHS_SHORT[r.month]}</TableCell>
                    <TableCell>{r.client}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 320,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={r.lineName}
                    >
                      {r.lineName}
                    </TableCell>
                    <TableCell>
                      {r.technicians.join(", ") || "Sin asignar"}
                    </TableCell>
                    <Num sx={{ fontWeight: 600 }}>
                      {unitMoney(r.grossUnitCost)}
                    </Num>
                    <Num>{unitMoney(realUnitCost(r))}</Num>
                    <Num>{eur(r.sale)}</Num>
                    <Num muted>{eur(r.realCost)}</Num>
                    <Num>{pct(r.sale ? 1 - r.realCost / r.sale : NaN, 1)}</Num>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Section>
  );
};

export default MarginsTab;
